import type { Scope } from './scope';

/**
 * Live EEG band-power computation — for the rolling Delta/Theta/Alpha/Beta/
 * Gamma chart on the recording screen.
 *
 * Returns each band's share of the total power (so the five values sum to 1)
 * rather than absolute power, so the display is comparable across sessions
 * and electrode contact quality. Computed by taking the last second of EEG
 * (`WINDOW` samples) per channel, de-meaning, applying a Hann window,
 * running a radix-2 FFT, and integrating |X[k]|² over each band's bins.
 */

const EEG_SAMPLE_RATE = 256;
/** FFT window length — one second of EEG, power of two. */
const WINDOW = 256;

/** Standard EEG band ranges (Hz). With sampleRate=256 and N=256, bin k = k Hz. */
const BANDS = [
  { name: 'delta', loHz: 1, hiHz: 4 },
  { name: 'theta', loHz: 4, hiHz: 8 },
  { name: 'alpha', loHz: 8, hiHz: 13 },
  { name: 'beta', loHz: 13, hiHz: 30 },
  { name: 'gamma', loHz: 30, hiHz: 50 },
] as const;

export type BandName = (typeof BANDS)[number]['name'];

// Hann window — pre-computed once. Reduces spectral leakage at the window's
// edges (raw rectangular windowing would smear power across bins).
const HANN = new Float64Array(WINDOW);
for (let i = 0; i < WINDOW; i++) {
  HANN[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (WINDOW - 1)));
}

// Scratch FFT buffers — reused across calls to avoid allocations on the
// 500 ms tick.
const fftReal = new Float64Array(WINDOW);
const fftImag = new Float64Array(WINDOW);

/** In-place Cooley-Tukey radix-2 FFT. */
function fftRadix2(real: Float64Array, imag: Float64Array): void {
  const n = real.length;
  // Bit-reverse the input order.
  let j = 0;
  for (let i = 1; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = real[i];
      const ti = imag[i];
      real[i] = real[j];
      imag[i] = imag[j];
      real[j] = tr;
      imag[j] = ti;
    }
  }
  // Butterflies.
  for (let size = 2; size <= n; size <<= 1) {
    const half = size >> 1;
    const angle = (-2 * Math.PI) / size;
    const wReal = Math.cos(angle);
    const wImag = Math.sin(angle);
    for (let i = 0; i < n; i += size) {
      let cReal = 1;
      let cImag = 0;
      for (let k = 0; k < half; k++) {
        const tReal = cReal * real[i + k + half] - cImag * imag[i + k + half];
        const tImag = cReal * imag[i + k + half] + cImag * real[i + k + half];
        real[i + k + half] = real[i + k] - tReal;
        imag[i + k + half] = imag[i + k] - tImag;
        real[i + k] += tReal;
        imag[i + k] += tImag;
        const nReal = cReal * wReal - cImag * wImag;
        const nImag = cReal * wImag + cImag * wReal;
        cReal = nReal;
        cImag = nImag;
      }
    }
  }
}

/**
 * Compute relative band powers for the given EEG channels, summed across
 * channels. Returns each band's share of the total in [0, 1], or `null` if
 * no channel has enough samples yet.
 */
export function eegBandPowers(
  scope: Scope,
  channels: string[],
): Record<BandName, number> | null {
  let totalDelta = 0;
  let totalTheta = 0;
  let totalAlpha = 0;
  let totalBeta = 0;
  let totalGamma = 0;
  let usable = 0;

  for (const channel of channels) {
    const series = scope.read(channel);
    if (series.length < WINDOW) continue;
    const start = series.length - WINDOW;

    // De-mean to remove DC offset, then apply the Hann window.
    let mean = 0;
    for (let i = 0; i < WINDOW; i++) mean += series[start + i];
    mean /= WINDOW;
    for (let i = 0; i < WINDOW; i++) {
      fftReal[i] = (series[start + i] - mean) * HANN[i];
      fftImag[i] = 0;
    }
    fftRadix2(fftReal, fftImag);

    // Integrate |X[k]|² over each band's bins.
    for (const band of BANDS) {
      let p = 0;
      for (let k = band.loHz; k < band.hiHz; k++) {
        p += fftReal[k] * fftReal[k] + fftImag[k] * fftImag[k];
      }
      if (band.name === 'delta') totalDelta += p;
      else if (band.name === 'theta') totalTheta += p;
      else if (band.name === 'alpha') totalAlpha += p;
      else if (band.name === 'beta') totalBeta += p;
      else totalGamma += p;
    }
    usable += 1;
  }

  if (usable === 0) return null;
  const total = totalDelta + totalTheta + totalAlpha + totalBeta + totalGamma;
  if (total <= 0) return null;
  return {
    delta: totalDelta / total,
    theta: totalTheta / total,
    alpha: totalAlpha / total,
    beta: totalBeta / total,
    gamma: totalGamma / total,
  };
}

/** Exposed for callers that want to know the band definitions. */
export { BANDS, EEG_SAMPLE_RATE, WINDOW };
